import {GridColumnProps, GridRowModel, ValidRowModel} from './types';
import {IntlShape} from 'react-intl';
import React, {useState} from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import classNames from 'classnames';
import styles from './grid.module.scss';
import GridRowCell from './grid-cell';

interface Props {
    index: number;
    columns: GridColumnProps[];
    row: Readonly<ValidRowModel>;
    intl: IntlShape;
    lazy: boolean;
    noHeader: boolean;
    onRowEditStop?: (row: GridRowModel, state: GridRowModel) => Promise<void>;
    onRowClick?: (row: GridRowModel, event: React.MouseEvent<HTMLDivElement>) => void;
}

const GridRow = (props: Props) => {
    const { row, intl, noHeader, onRowClick } = props;
    const columns = props.columns.filter((x) => x.visible === undefined || x.visible);
    const [editState, setEditState] = useState<GridRowModel | null>(null);
    const [rowEditing, setRowEditing] = useState<boolean>(false);
    const [updating, setUpdating] = useState(false);

    return React.useMemo(() => {
        const handleBlur = (reason?: string) => {
            ;
        }

        const commit = (toCommit: GridRowModel) => {
            if (toCommit !== null) {
                setUpdating(true);
                props.onRowEditStop?.(row, toCommit).finally(() => {
                    setEditState(null);
                    setUpdating(false);
                });
            }
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const handleCellEditStart = (field: string, value: any) => {
            if (!rowEditing) {
                setRowEditing(true)
            }
        }

        const handleCellEdit = (field: string, value: any) => {
            if (!rowEditing) {
                return;
            }

            const toCommit: GridRowModel = { id: 0, [field]: value };
            setRowEditing(false);
            commit(toCommit);
        }

        const cells: React.ReactNode[] = [];
        let cellIndex = 0;
        for (let index = 0; index < columns.length; index++) {
            const column = columns[index];
            let span = column.colSpan ? column.colSpan(row): null;
            if (span !== null && index + span >= columns.length - 1) {
                span = null;
            }
            cells.push(<GridRowCell key={cellIndex}
                field={column.field}
                row={row}
                column={column}
                intl={intl}
                rowEdit={rowEditing}
                setBackground={column.setBackground}
                onCellEditStart={handleCellEditStart}
                onCellEditStop={handleCellEdit}
                editState={editState}
                lazyDataLoad={props.lazy}
                displayPlaceholder={noHeader}
                gridColumn={{ s: index + 1, e: span ? index + span + 1 : index + 1 }}
                disabled={updating}
            />)

            if (span) {
                index += span - 1;
            }

            cellIndex++;
        }

        return (<ClickAwayListener key={row.id} onClickAway={() => handleBlur()}>
            {
                row && <div id={row.id} onKeyDown={(event) => handleBlur(event.key)} className={styles.row}>
                    <div className={classNames({
                        [styles.rowContainer]: true,
                        [styles.active]: rowEditing
                    })}
                    onClick={onRowClick ? (event) => onRowClick(row, event) : undefined}>
                        { cells }
                    </div>
                </div>
            }
        </ClickAwayListener>)
    }, [row, columns, editState, rowEditing, onRowClick]);
}

export default GridRow;